import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Mic } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Alert, Badge, Card, EmptyState, Grid, Select, Stack, Text } from '../components/atoms';
import { Heading } from '../components/atoms/Typography/Heading';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea, useIsAdmin } from '../components/admin/AdminArea';
import { AdminTable } from '../components/admin/AdminTable';
import { formatNumber } from '../components/admin/adminFormat';
import {
  VOICE_QUALITY_PERIODS,
  fetchAdminVoiceQuality,
  type AdminVoiceQuality,
  type VoiceQualityKpi,
  type VoiceQualityPeriod,
  type VoiceQualitySuggestionKind,
  type VoiceQualityTiming,
} from '../services/adminService';
import { REVIEW_REASONS, SUGGESTION_KINDS } from '../types/capture';
import './AdminVoiceQualityPage.css';

const DEFAULT_PERIOD: VoiceQualityPeriod = 28;
const KPI_IDS = [
  'weekly_voice_users_share',
  'captures_saved_share',
  'stop_to_saved_median_seconds',
  'measurement_accepted_unedited_rate',
  'task_accepted_unedited_rate',
  'thumbs_up_share',
  'reanalyses_per_note',
  'cost_per_note_usd',
] as const;
const KINDS = SUGGESTION_KINDS;
const REASONS = REVIEW_REASONS;
const ERROR_CODES = ['TRANSCRIPTION_FAILED', 'ANALYSIS_FAILED', 'AUDIO_EXPIRED', 'NO_SPEECH', 'UNKNOWN'] as const;

type T = ReturnType<typeof useTranslation>['t'];

function known<K extends string>(list: readonly K[], value: string): value is K {
  return (list as readonly string[]).includes(value);
}

const P = 'adminArea.voiceQuality';

/** Formats rates, durations, counts and US$ for the current language; '—' for null (no data / suppressed). */
function useFormat() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const percent = (v: number | null): string => (v === null ? '—' : t(`${P}.percent`, { value: formatNumber(v * 100, lang, 1) }));
  const duration = (s: number | null): string => {
    if (s === null) return '—';
    if (s < 120) return t(`${P}.seconds`, { value: formatNumber(s, lang, 1) });
    if (s < 7200) return t(`${P}.minutes`, { value: formatNumber(s / 60, lang, 1) });
    return t(`${P}.hours`, { value: formatNumber(s / 3600, lang, 1) });
  };
  const usd = (v: number | null): string => (v === null ? '—' : t(`${P}.usd`, { value: formatNumber(v, lang, 4) }));
  const num = (v: number | null, digits = 0): string => (v === null ? '—' : formatNumber(v, lang, digits));
  const kpiValue = (k: Pick<VoiceQualityKpi, 'unit'>, v: number | null): string => {
    if (k.unit === 'ratio') return percent(v);
    if (k.unit === 'seconds') return duration(v);
    if (k.unit === 'usd') return usd(v);
    return num(v, 2);
  };
  return { t, lang, percent, duration, usd, num, kpiValue };
}

function kindLabel(t: T, kind: string): string {
  return known(KINDS, kind) ? t(`${P}.kinds.${kind}`) : kind;
}

function KpiCard({ kpi }: { kpi: VoiceQualityKpi }) {
  const { t, kpiValue } = useFormat();
  const symbol = kpi.comparator === 'gte' ? '≥' : kpi.comparator === 'lte' ? '≤' : '<';
  const label = known(KPI_IDS, kpi.id) ? t(`${P}.kpi.${kpi.id}`) : kpi.id;
  const status =
    kpi.met === null
      ? { tone: 'neutral' as const, text: t(`${P}.noData`) }
      : kpi.met
        ? { tone: 'success' as const, text: t(`${P}.met`) }
        : { tone: 'warning' as const, text: t(`${P}.notMet`) };
  return (
    <li className="admin-voice-quality__kpi">
      <Card padding="md" className="admin-voice-quality__kpi-card">
        <Stack gap="2">
          <Heading level={3} className="admin-voice-quality__kpi-title">
            {label}
          </Heading>
          <Text size="xl" weight="semibold" className="admin-voice-quality__kpi-value">
            {kpiValue(kpi, kpi.value)}
          </Text>
          <Text size="sm" color="secondary">
            {t(`${P}.target`, { target: `${symbol} ${kpiValue(kpi, kpi.target)}` })}
          </Text>
          <div>
            <Badge tone={status.tone}>{status.text}</Badge>
          </div>
        </Stack>
      </Card>
    </li>
  );
}

function Summary({ items }: { items: Array<{ key: string; term: string; value: string }> }) {
  return (
    <dl className="rg-admin-summary">
      {items.map((item) => (
        <div key={item.key} className="rg-admin-summary__item">
          <dt className="rg-admin-summary__term">{item.term}</dt>
          <dd className="rg-admin-summary__value">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SuggestionTable({ caption, rows, notesLabel }: { caption: string; rows: VoiceQualitySuggestionKind[]; notesLabel: string }) {
  const { t, num, percent } = useFormat();
  if (rows.length === 0) return <Text color="secondary">{t(`${P}.noSuggestions`, { caption })}</Text>;
  return (
    <AdminTable caption={caption}>
      <thead>
        <tr>
          <th scope="col">{t(`${P}.col.kind`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{notesLabel}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.suggested`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.accepted`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.dismissed`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.expired`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.acceptRate`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.withoutEdit`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.editRate`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.dismissRate`)}</th>
          <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.lowConfidence`)}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={`${row.source}-${row.kind}`}>
            <th scope="row">{kindLabel(t, row.kind)}</th>
            <td className="rg-admin-table__numeric">{num(row.notes)}</td>
            <td className="rg-admin-table__numeric">{num(row.suggested)}</td>
            <td className="rg-admin-table__numeric">{num(row.accepted)}</td>
            <td className="rg-admin-table__numeric">{num(row.dismissed)}</td>
            <td className="rg-admin-table__numeric">{num(row.expired)}</td>
            <td className="rg-admin-table__numeric">{percent(row.acceptRate)}</td>
            <td className="rg-admin-table__numeric">{percent(row.acceptedWithoutEditRate)}</td>
            <td className="rg-admin-table__numeric">{percent(row.editRate)}</td>
            <td className="rg-admin-table__numeric">{percent(row.dismissRate)}</td>
            <td className="rg-admin-table__numeric">
              {num(row.lowConfidence)}
              {row.lowConfidenceShare !== null && (
                <Text as="span" size="xs" className="rg-admin-table__muted admin-voice-quality__share">
                  {percent(row.lowConfidenceShare)}
                </Text>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </AdminTable>
  );
}

function Report({ data }: { data: AdminVoiceQuality }) {
  const { t, num, percent, duration, usd } = useFormat();
  const kpis = data.kpis;
  const c = data.captures;
  const timings: Array<{ key: string; label: string; value: VoiceQualityTiming }> = [
    { key: 'up', label: t(`${P}.timing.uploadToProcessed`), value: c.uploadToProcessed },
    { key: 'pa', label: t(`${P}.timing.processedToApplied`), value: c.processedToApplied },
    { key: 'ua', label: t(`${P}.timing.uploadToApplied`), value: c.uploadToApplied },
  ];
  const voiceRows = data.suggestions.filter((s) => s.source === 'voice');
  const typedRows = data.suggestions.filter((s) => s.source === 'typed');
  const sourceLabel = (s: 'voice' | 'typed'): string => (s === 'voice' ? t(`${P}.sourceVoice`) : t(`${P}.sourceTyped`));

  return (
    <>
      <Section title={t(`${P}.kpisTitle`)} description={t(`${P}.privacyNote`, { min: data.minBucketSize })}>
        <Grid as="ul" minItemWidth="sm" gap="4" className="admin-voice-quality__kpis">
          {kpis.map((kpi) => (
            <KpiCard key={kpi.id} kpi={kpi} />
          ))}
        </Grid>
      </Section>

      <Section title={t(`${P}.capturesTitle`)}>
        <Stack gap="6">
          <Summary
            items={[
              { key: 'total', term: t(`${P}.captures.total`), value: num(c.total) },
              { key: 'saved', term: t(`${P}.captures.saved`), value: num(c.saved) },
              { key: 'review', term: t(`${P}.captures.reviewWaiting`), value: num(c.reviewWaiting) },
              { key: 'discarded', term: t(`${P}.captures.discarded`), value: num(c.discarded) },
              { key: 'progress', term: t(`${P}.captures.inProgress`), value: num(c.inProgress) },
              { key: 'failed', term: t(`${P}.captures.failed`), value: num(c.failed) },
              { key: 'savedShare', term: t(`${P}.captures.savedShare`), value: percent(c.savedShare) },
              { key: 'textOnly', term: t(`${P}.captures.textOnly`), value: `${num(c.textOnly)} · ${percent(c.textOnlyShare)}` },
              { key: 'duration', term: t(`${P}.captures.medianDuration`), value: duration(c.medianDurationSeconds) },
            ]}
          />
          <AdminTable caption={t(`${P}.timingsCaption`)}>
            <thead>
              <tr>
                <th scope="col">{t(`${P}.timing.step`)}</th>
                <th scope="col" className="rg-admin-table__numeric">{t(`${P}.timing.median`)}</th>
                <th scope="col" className="rg-admin-table__numeric">{t(`${P}.timing.p90`)}</th>
                <th scope="col" className="rg-admin-table__numeric">{t(`${P}.timing.count`)}</th>
              </tr>
            </thead>
            <tbody>
              {timings.map((row) => (
                <tr key={row.key}>
                  <th scope="row">{row.label}</th>
                  <td className="rg-admin-table__numeric">{duration(row.value.medianSeconds)}</td>
                  <td className="rg-admin-table__numeric">{duration(row.value.p90Seconds)}</td>
                  <td className="rg-admin-table__numeric">{num(row.value.count)}</td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
          {c.byLanguage.length > 0 && (
            <AdminTable caption={t(`${P}.languagesCaption`)}>
              <thead>
                <tr>
                  <th scope="col">{t(`${P}.col.language`)}</th>
                  <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.count`)}</th>
                </tr>
              </thead>
              <tbody>
                {c.byLanguage.map((row) => (
                  <tr key={row.language}>
                    <th scope="row">{row.language.toUpperCase()}</th>
                    <td className="rg-admin-table__numeric">{num(row.count)}</td>
                  </tr>
                ))}
              </tbody>
            </AdminTable>
          )}
        </Stack>
      </Section>

      <Section title={t(`${P}.suggestionsTitle`)} description={t(`${P}.suppressedNote`, { min: data.minBucketSize })}>
        <Stack gap="6">
          <SuggestionTable caption={t(`${P}.suggestionsVoiceCaption`)} rows={voiceRows} notesLabel={t(`${P}.col.notes`)} />
          <SuggestionTable caption={t(`${P}.suggestionsTypedCaption`)} rows={typedRows} notesLabel={t(`${P}.col.entries`)} />
        </Stack>
      </Section>

      <Section title={t(`${P}.errorsTitle`)} description={t(`${P}.errorsDescription`)}>
        <Stack gap="6">
          {data.reviewReasons.length === 0 ? (
            <Text color="secondary">{t(`${P}.noReasons`)}</Text>
          ) : (
            <AdminTable caption={t(`${P}.reasonsCaption`)}>
              <thead>
                <tr>
                  <th scope="col">{t(`${P}.col.reason`)}</th>
                  <th scope="col">{t(`${P}.col.source`)}</th>
                  <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.count`)}</th>
                  <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.accepted`)}</th>
                  <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.dismissed`)}</th>
                </tr>
              </thead>
              <tbody>
                {data.reviewReasons.map((row) => (
                  <tr key={`${row.source}-${row.reason}`}>
                    <th scope="row">{known(REASONS, row.reason) ? t(`${P}.reasons.${row.reason}`) : row.reason}</th>
                    <td>{sourceLabel(row.source)}</td>
                    <td className="rg-admin-table__numeric">{num(row.count)}</td>
                    <td className="rg-admin-table__numeric">{num(row.accepted)}</td>
                    <td className="rg-admin-table__numeric">{num(row.dismissed)}</td>
                  </tr>
                ))}
              </tbody>
            </AdminTable>
          )}
          {c.failedByErrorCode.length === 0 ? (
            <Text color="secondary">{t(`${P}.noFailures`)}</Text>
          ) : (
            <AdminTable caption={t(`${P}.failedCaption`)}>
              <thead>
                <tr>
                  <th scope="col">{t(`${P}.col.code`)}</th>
                  <th scope="col" className="rg-admin-table__numeric">{t(`${P}.col.count`)}</th>
                </tr>
              </thead>
              <tbody>
                {c.failedByErrorCode.map((row) => (
                  <tr key={row.code}>
                    <th scope="row">{known(ERROR_CODES, row.code) ? t(`${P}.errorCodes.${row.code}`) : row.code}</th>
                    <td className="rg-admin-table__numeric">{num(row.count)}</td>
                  </tr>
                ))}
              </tbody>
            </AdminTable>
          )}
        </Stack>
      </Section>

      <Section title={t(`${P}.feedbackTitle`)}>
        <Summary
          items={[
            { key: 'up', term: t(`${P}.feedback.thumbsUp`), value: num(data.thumbs.up) },
            { key: 'down', term: t(`${P}.feedback.thumbsDown`), value: num(data.thumbs.down) },
            { key: 'upShare', term: t(`${P}.feedback.upShare`), value: percent(data.thumbs.upShare) },
            { key: 'reanalyses', term: t(`${P}.feedback.reanalyses`), value: num(data.reanalyses.total) },
            { key: 'reanalysesPerNote', term: t(`${P}.feedback.reanalysesPerNote`), value: num(data.reanalyses.perNote, 2) },
            { key: 'notesMetered', term: t(`${P}.feedback.notesMetered`), value: num(data.cost.notesMetered) },
            { key: 'totalCost', term: t(`${P}.feedback.totalCost`), value: usd(data.cost.totalUsd) },
            { key: 'costPerNote', term: t(`${P}.feedback.costPerNote`), value: usd(data.cost.usdPerNote) },
            { key: 'creditsPerNote', term: t(`${P}.feedback.creditsPerNote`), value: num(data.cost.creditsPerNote, 2) },
            { key: 'guided', term: t(`${P}.feedback.guidedSessions`), value: `${num(data.cost.guidedSessions.sessions)} · ${usd(data.cost.guidedSessions.costUsd)}` },
            { key: 'typed', term: t(`${P}.feedback.typedDetection`), value: `${num(data.cost.typedTaskDetection.calls)} · ${usd(data.cost.typedTaskDetection.costUsd)}` },
          ]}
        />
      </Section>

      <Section title={t(`${P}.usersTitle`)} description={t(`${P}.usersNote`)}>
        <Summary
          items={[
            { key: 'weeklyVoice', term: t(`${P}.users.weeklyVoice`), value: num(data.users.weekly.voiceUsers) },
            { key: 'weeklyActive', term: t(`${P}.users.weeklyActive`), value: num(data.users.weekly.activeUsers) },
            { key: 'weeklyShare', term: t(`${P}.users.weeklyShare`), value: percent(data.users.weekly.share) },
            { key: 'periodVoice', term: t(`${P}.users.periodVoice`), value: num(data.users.period.voiceUsers) },
            { key: 'periodActive', term: t(`${P}.users.periodActive`), value: num(data.users.period.activeUsers) },
          ]}
        />
      </Section>
    </>
  );
}

/**
 * Admin → Voice journal: the voice journal's success criteria and quality
 * figures (GET /api/v1/admin/voice-quality?days=7|28|90) — aggregated on the
 * server, no content, no user data (docs/voiceFeature.md, F5).
 */
export function AdminVoiceQualityPage() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const [days, setDays] = useState<VoiceQualityPeriod>(DEFAULT_PERIOD);
  const query = useQuery({
    queryKey: ['admin', 'voice-quality', days],
    queryFn: () => fetchAdminVoiceQuality(days),
    enabled: isAdmin,
    placeholderData: keepPreviousData,
  });
  const data = query.data;
  const isEmpty = data !== undefined && data.captures.total === 0 && data.suggestions.length === 0;

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t(`${P}.description`)} />
          <AdminArea>
            <Section title={t(`${P}.periodTitle`)}>
              <Card padding="md">
                <div className="rg-admin-form">
                  <Select
                    id="admin-voice-quality-period"
                    label={t(`${P}.period`)}
                    value={String(days)}
                    onChange={(e) => setDays(Number(e.target.value) as VoiceQualityPeriod)}
                    options={VOICE_QUALITY_PERIODS.map((d) => ({ value: String(d), label: t(`${P}.periodOption`, { days: d }) }))}
                    fullWidth
                  />
                </div>
              </Card>
              {data && !data.retention.complete && (
                <Alert tone="info" live="off" title={t(`${P}.retentionTitle`)}>
                  {t(`${P}.retentionNote`, { days: data.retention.days })}
                </Alert>
              )}
            </Section>

            {query.isLoading && (
              <LiveRegion>
                <Text>{t(`${P}.loading`)}</Text>
              </LiveRegion>
            )}
            {query.isError && (
              <LoadErrorState title={t(`${P}.loadError`)} onRetry={() => void query.refetch()} titleLevel={2} />
            )}
            {!query.isLoading && !query.isError && isEmpty && (
              <EmptyState
                icon={<Mic size={48} strokeWidth={1.5} aria-hidden="true" />}
                headline={t(`${P}.emptyTitle`)}
                description={t(`${P}.emptyMessage`)}
                actionLabel={days === 90 ? t(`${P}.emptyActionReload`) : t(`${P}.emptyActionLonger`)}
                onAction={days === 90 ? () => void query.refetch() : () => setDays(90)}
                headingLevel={2}
              />
            )}
            {!query.isLoading && !query.isError && data && !isEmpty && <Report data={data} />}
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminVoiceQualityPage;
